import React, { useMemo, ComponentProps } from 'react';

import styled from 'styled-components';

import {
  useCompanies,
  useFormattedUser,
  useUser,
  useUserByAutodeskId,
} from '@adsk/alloy-provider';
import Avatar from '@adsk/alloy-react-avatar';
import { Box, Flex } from '@adsk/alloy-react-box';
import theme from '@adsk/alloy-react-theme';
import Tooltip from '@adsk/alloy-react-tooltip';

import { MentionData, MENTION_TYPES } from '../../consts';
import { useTranslation } from '../../i18n';

const StyledSpan = styled.span<{ $isCurrentUser: boolean }>`
  ${({ $isCurrentUser }) =>
    $isCurrentUser && {
      background: theme.colors.yellowOrange100,
      '&:hover': {
        background: theme.colors.yellowOrange300,
      },
    }}
`;

type MentionUserTooltipProps = Omit<
  ComponentProps<typeof Tooltip>,
  'content'
> & {
  mention: MentionData;
};

export const MentionUserTooltip = ({
  mention,
  children,
  ...props
}: MentionUserTooltipProps) => {
  const { t } = useTranslation();
  const companies = useCompanies();
  const { user: currentUser } = useUser();
  const { user } = useUserByAutodeskId(mention?.id?.toString() || '', {
    includeDeleted: true,
  });

  const { formatDisplayUser } = useFormattedUser();

  const formattedUserName = useMemo(
    () =>
      (mention && formatDisplayUser(mention.id).displayName) ||
      mention?.name ||
      t('NOT_AVAILABLE')?.toString(),
    [formatDisplayUser, mention, t],
  );
  return (
    <Tooltip
      {...props}
      content={
        <Flex
          data-testid={`MentionUserTooltip_Content_${mention?.id}`}
          alignItems="center"
          style={{ wordBreak: 'break-all' }}
        >
          <Avatar
            name={formattedUserName}
            imageUrl={user?.imageUrl}
            style={{
              marginRight: 10,
            }}
          />
          <span>
            <Box typography="labelMedium" color="charcoal900">
              {formattedUserName}
            </Box>
            <Box typography="labelMedium" color="charcoal700">
              {
                companies.data.find((company) => company.id === user?.companyId)
                  ?.name
              }
            </Box>
          </span>
        </Flex>
      }
      triggerTarget={Tooltip.TRIGGER_TARGETS.TOOLTIP}
      variant={Tooltip.VARIANTS.LIGHT}
    >
      <StyledSpan
        $isCurrentUser={
          mention?.type === MENTION_TYPES.USER &&
          currentUser?.id === mention?.id
        }
        data-testid={`MentionUserTooltip_Trigger_${mention?.id}`}
      >
        @{children || formattedUserName}
      </StyledSpan>
    </Tooltip>
  );
};
